<template>
  <div class="bottom-navigation-wrapper">
    <!-- Expandable Menu Overlay -->
    <div
      v-if="isMenuOpen"
      class="menu-overlay"
      data-testid="action-menu-overlay"
      @click="closeMenu"
    ></div>

    <!-- Expandable Action Menu -->
    <transition name="menu-slide">
      <div
        v-if="isMenuOpen"
        class="action-menu"
        data-testid="action-menu"
      >
        <div class="action-menu-content">
          <!-- Action buttons -->
          <button
            v-for="action in actions"
            :key="action.id"
            type="button"
            class="action-button"
            :data-testid="`action-${action.id}`"
            @click="handleActionClick(action)"
          >
            <BaseIcon :icon="action.icon" class="action-icon" :aria-hidden="true" />
            <span class="action-label">{{ action.label }}</span>
          </button>
        </div>
      </div>
    </transition>

    <!-- Bottom Tab Bar -->
    <nav class="tab-bar" data-testid="bottom-tab-bar">
      <!-- Left tabs -->
      <router-link
        v-for="tab in leftTabs"
        :key="tab.id"
        :to="tab.to"
        class="tab-button"
        :class="{ active: isTabActive(tab) }"
        :data-testid="`tab-${tab.id}`"
        :aria-label="tab.label"
        :title="tab.label"
        @click="closeMenu"
      >
        <div class="icon-container">
          <BaseIcon :icon="tab.icon" :size="32" :aria-hidden="true" />
        </div>
        <span class="tab-label">{{ tab.label }}</span>
      </router-link>

      <!-- Central FAB Spacer -->
      <div class="fab-spacer" :aria-hidden="true"></div>

      <!-- Right tabs -->
      <router-link
        v-for="tab in rightTabs"
        :key="tab.id"
        :to="tab.to"
        class="tab-button"
        :class="{ active: isTabActive(tab) }"
        :data-testid="`tab-${tab.id}`"
        :aria-label="tab.label"
        :title="tab.label"
        @click="closeMenu"
      >
        <div class="icon-container">
          <BaseIcon :icon="tab.icon" :size="32" :aria-hidden="true" />
        </div>
        <span class="tab-label">{{ tab.label }}</span>
      </router-link>

      <!-- Tab bar cutout background -->
      <div class="tab-bar-cutout" :aria-hidden="true"></div>
    </nav>

    <!-- Central FAB -->
    <button
      type="button"
      class="central-fab"
      :class="{ 'fab-active': isMenuOpen }"
      data-testid="central-fab"
      :aria-label="isMenuOpen ? 'Close quick actions' : 'Open quick actions'"
      :aria-expanded="isMenuOpen"
      @click="toggleMenu"
    >
      <BaseIcon
        :icon="isMenuOpen ? X : Plus"
        class="fab-icon"
        :aria-hidden="true"
      />
      <span class="fab-label">{{ isMenuOpen ? 'Close' : 'Add' }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, type Component } from 'vue';
import { Plus, X } from 'lucide-vue-next';
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router';
import BaseIcon from '@/components/ui/BaseIcon.vue';

export interface TabItem {
  id: string;
  label: string;
  icon: Component;
  to: RouteLocationRaw;
}

export interface ActionItem {
  id: string;
  label: string;
  icon: Component;
}

interface Props {
  leftTabs: TabItem[];
  rightTabs: TabItem[];
  actions: ActionItem[];
}

defineProps<Props>();

const emit = defineEmits<{
  actionClick: [actionId: string];
}>();

const route = useRoute();
const router = useRouter();
const isMenuOpen = ref(false);

const isTabActive = (tab: TabItem): boolean => {
  const resolved = router.resolve(tab.to);
  return route.path === resolved.path || route.path.startsWith(`${resolved.path}/`);
};

const toggleMenu = (): void => {
  isMenuOpen.value = !isMenuOpen.value;
};

const closeMenu = (): void => {
  isMenuOpen.value = false;
};

const handleActionClick = (action: ActionItem): void => {
  closeMenu();
  emit('actionClick', action.id);
};
</script>

<style scoped>
/* Wrapper */
.bottom-navigation-wrapper {
  --bottom-nav-cutout-size: calc(var(--cta-button-size) + var(--space-lg) - var(--space-xs));
  --bottom-nav-safe-area: var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px));

  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding-bottom: var(--bottom-nav-safe-area);
  box-sizing: border-box;
  background: var(--color-background-secondary);
  z-index: var(--z-sticky, 200);
}

/* Menu Overlay */
.menu-overlay {
  position: fixed;
  inset: 0;
  background: color-mix(in srgb, black 22%, transparent);
  z-index: 1;
}

/* Expandable Action Menu */
.action-menu {
  --menu-radius-top: 16px;
  --menu-radius-bottom: 12px;
  
  position: fixed;
  /* Position menu to sit just above the tab bar, not behind it */
  bottom: calc(var(--tab-bar-height) + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + var(--space-sm));
  left: 1rem;
  right: 1rem;
  z-index: 2;
  background: var(--color-background-secondary);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-sm);
  
  /* Rounded corners - top larger, bottom smaller */
  border-radius: var(--menu-radius-top) var(--menu-radius-top) var(--menu-radius-bottom) var(--menu-radius-bottom);
}

.action-menu-content {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-md);
  padding: var(--space-lg) var(--space-lg) var(--space-xl);
}

.action-button {
  appearance: none;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  background: var(--color-background-secondary);
  box-shadow: none;
  padding: var(--space-md) var(--space-xs);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-xs);
  min-height: 88px;
  cursor: pointer;
  transition: transform var(--transition-fast), box-shadow var(--transition-fast);
}

.action-button:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.action-button:active {
  transform: translateY(0) scale(0.98);
  box-shadow: var(--shadow-sm);
}

.action-icon {
  width: 28px;
  height: 28px;

  color: var(--color-primary);
}

.action-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  text-align: center;
}

/* Menu slide animation */
.menu-slide-enter-active,
.menu-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.menu-slide-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.menu-slide-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Tab Bar */
.tab-bar {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  width: 100%;
  height: var(--tab-bar-height);
  min-height: var(--tab-bar-height);
  padding: 0 var(--space-md);
  box-sizing: border-box;
  background: var(--color-background-secondary);
  border-top: 1px solid var(--color-border-light);
  box-shadow: 0 -2px 8px oklch(0% 0 0 / 0.04);
  z-index: 3;
  overflow: hidden;
}

/* Tab bar cutout for FAB */
.tab-bar-cutout {
  content: "";
  position: absolute;
  top: calc(var(--space-sm) * -1);
  left: 50%;
  transform: translateX(-50%);
  width: var(--bottom-nav-cutout-size);
  height: var(--bottom-nav-cutout-size);
  background: var(--color-background-secondary);
  border-radius: calc(var(--bottom-nav-cutout-size) / 2) calc(var(--bottom-nav-cutout-size) / 2) 0 0;
  z-index: -1;
}

/* Tab bar background extension */
.tab-bar::before {
  content: "";
  position: absolute;
  top: calc(var(--space-sm) * -1);
  left: 0;
  right: 0;
  height: var(--space-sm);
  background: var(--color-background-secondary);
  z-index: -2;
}

.tab-button {
  flex: 1 1 0;
  max-width: none;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center; /* keep them centered */
  justify-content: center;
  gap: 0;
  padding: 0; /* Remove padding entirely, let icon container dictate height */
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: color var(--transition-fast), background-color var(--transition-fast);
  cursor: pointer;
  border: none;
  background: none;
  position: relative;
  z-index: 1;
}

.icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  width: 44px;
  border-radius: 50%;
  transition: all var(--transition-base);
}

.tab-button:hover .icon-container {
  background: color-mix(in srgb, var(--color-text-secondary) 8%, transparent);
}

.tab-button:hover {
  color: var(--color-text-primary);
}

.tab-button.active {
  color: var(--color-primary);
}

.tab-button.active .icon-container {
  background: color-mix(in srgb, var(--color-primary) 12%, transparent);
}

.tab-button.active svg {
  color: var(--color-primary) !important;
}


.fab-spacer {
  flex: 0 0 var(--cta-button-size);
  height: var(--tab-bar-height);
  pointer-events: none;
}

/* Central FAB */
.central-fab {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 4;
  width: var(--cta-button-size);
  height: var(--cta-button-size);
  border-radius: 50%;
  background: var(--color-primary);
  border: 2px solid var(--color-background-secondary);
  box-shadow: var(--shadow-md);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all var(--transition-base);
}

.central-fab:hover {
  transform: translate(-50%, -50%) scale(1.02);
  box-shadow: var(--shadow-lg);
}

.central-fab:active {
  transform: translate(-50%, -50%) scale(0.95);
}

.central-fab.fab-active {
  transform: translate(-50%, -50%) rotate(90deg);
}

.fab-icon {
  width: 46px;
  height: 46px;
  color: white;
  transition: transform var(--transition-base);
}

/* Tab label — hidden on mobile, shown on desktop */
.tab-label {
  display: none;
}

/* FAB label — hidden on mobile, shown on desktop */
.fab-label {
  display: none;
}

/* ==========================================
   Desktop Sidebar Layout (≥ 1024px)
   ========================================== */
@media (min-width: 1024px) {
  /* Transform from bottom bar to left sidebar */
  .bottom-navigation-wrapper {
    bottom: auto;
    top: 0;
    right: auto;
    width: 240px;
    height: 100vh;
    height: 100dvh;
    border-right: 1px solid var(--color-border-light);
    box-shadow: var(--shadow-sm);
    display: flex;
    flex-direction: column;
  }

  /* Tab bar becomes a vertical column */
  .tab-bar {
    flex-direction: column;
    height: 100%;
    width: 100%;
    /* Top padding makes room for the repositioned FAB (space-md + 44px + space-md) */
    padding: calc(var(--space-md) * 2 + 44px) 0 var(--space-md);
    align-items: stretch;
    justify-content: flex-start;
    box-shadow: none;
    overflow-y: auto;
    overflow-x: visible;
  }

  /* Hide mobile-only decorative elements */
  .tab-bar::before,
  .tab-bar-cutout,
  .fab-spacer {
    display: none;
  }

  /* Tab buttons become horizontal rows */
  .tab-button {
    flex: 0 0 auto;
    flex-direction: row;
    justify-content: flex-start;
    padding: var(--space-sm) var(--space-md);
    gap: var(--space-sm);
    height: auto;
    min-height: 44px;
    margin: 2px var(--space-sm);
    border-radius: var(--radius-md);
  }

  .icon-container {
    width: 32px;
    height: 32px;
    border-radius: var(--radius-sm);
    flex-shrink: 0;
  }

  .icon-container :deep(svg) {
    width: 20px;
    height: 20px;
  }

  /* Show tab labels on desktop */
  .tab-label {
    display: block;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: inherit;
  }

  /* Reposition FAB to top of sidebar as a full-width button */
  .central-fab {
    position: absolute;
    top: var(--space-md);
    left: var(--space-sm);
    right: var(--space-sm);
    bottom: auto;
    transform: none;
    width: auto;
    height: 44px;
    border-radius: var(--radius-md);
    border: none;
    flex-direction: row;
    justify-content: flex-start;
    padding: 0 var(--space-md);
    gap: var(--space-sm);
  }

  .central-fab:hover {
    transform: none;
    filter: brightness(1.1);
  }

  .central-fab:active {
    transform: none;
    filter: brightness(0.95);
  }

  .central-fab.fab-active {
    transform: none;
  }

  .fab-icon {
    width: 46px;
    height: 46px;
  }

  /* Show FAB label on desktop */
  .fab-label {
    display: block;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-semibold);
    color: white;
  }

  /* Action menu appears to the right of the sidebar */
  .action-menu {
    bottom: auto;
    top: calc(var(--space-md) * 2 + 44px + var(--space-sm));
    left: 248px;
    right: auto;
    width: 280px;
    border-radius: var(--radius-lg);
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .tab-button,
  .action-button,
  .central-fab {
    transition: none;
    transform: none !important;
  }

  .menu-slide-enter-active,
  .menu-slide-leave-active {
    transition: none;
  }

  .fab-icon {
    transition: none;
  }
}
</style>
